// src/lib/lead/merge/duplicates.ts
import type { ExtractedLeadV2, Lead } from "src/types/lead";

export type MergeSuggestion = { sourceId: string; reason: string };

export type DuplicateSeverity = "none" | "warn" | "block";

export type DuplicateEvaluation = {
  severity: DuplicateSeverity;
  matches: Lead[];
  reason: string | null;
  rule: string | null;
};

export type DuplicateDeps = {
  normalizePhone: (s: string | null | undefined) => string;
};

const normalizeName = (s: string | null | undefined) => (s || "").trim().toLowerCase();
const normalizeEmail = (s: string | null | undefined) => (s || "").trim().toLowerCase();

const namesCompatible = (
  a: ExtractedLeadV2,
  bFirst: string,
  bLast: string
): boolean => {
  const aFirst = normalizeName(a.first_name);
  const aLast = normalizeName(a.last_name);

  if ((!aFirst && !aLast) || (!bFirst && !bLast)) return false;

  const firstOk = aFirst && bFirst ? aFirst === bFirst : true;
  const lastOk = aLast && bLast ? aLast === bLast : true;

  return firstOk && lastOk;
};

export function findMergeSuggestion(
  target: Lead | null,
  pool: Lead[],
  deps: DuplicateDeps
): MergeSuggestion | null {
  if (!target || target.isTest) return null;

  const t = target.extracted;
  if (!t) return null;

  const tPhone = deps.normalizePhone(t.phone);
  const tEmail = normalizeEmail(t.email);
  const tFirst = normalizeName(t.first_name);
  const tLast = normalizeName(t.last_name);

  const hasPhone = tPhone.length >= 10;
  const hasEmail = !!tEmail && tEmail.includes("@");
  if (!hasPhone && !hasEmail) return null;

  const candidates = pool.filter((l) => {
    if (l.id === target.id) return false;
    if (l.isTest) return false;
    if (!l.extracted) return false;
    return true;
  });

  // Priority 1: same phone
  if (hasPhone) {
    const samePhone = candidates.find((l) => {
      const e = l.extracted!;
      const p = deps.normalizePhone(e.phone);
      if (p !== tPhone) return false;

      const bothHaveNames =
        (!!normalizeName(e.first_name) || !!normalizeName(e.last_name)) &&
        (!!tFirst || !!tLast);

      return bothHaveNames ? namesCompatible(e, tFirst, tLast) : true;
    });

    if (samePhone) {
      return { sourceId: samePhone.id, reason: "Possible duplicate: same phone number." };
    }
  }

  // Priority 2: same email
  if (hasEmail) {
    const sameEmail = candidates.find((l) => {
      const e = l.extracted!;
      const em = normalizeEmail(e.email);
      if (em !== tEmail) return false;

      const bothHaveNames =
        (!!normalizeName(e.first_name) || !!normalizeName(e.last_name)) &&
        (!!tFirst || !!tLast);

      return bothHaveNames ? namesCompatible(e, tFirst, tLast) : true;
    });

    if (sameEmail) {
      return { sourceId: sameEmail.id, reason: "Possible duplicate: same email address." };
    }
  }

  return null;
}

export function evaluateDuplicates(
  target: Lead,
  pool: Lead[],
  deps: DuplicateDeps
): DuplicateEvaluation {
  const t = target.extracted;
  if (!t || target.isTest) return { severity: "none", matches: [], reason: null, rule: null };

  const tPhone = deps.normalizePhone(t.phone);
  const tEmail = normalizeEmail(t.email);
  const tFirst = normalizeName(t.first_name);
  const tLast = normalizeName(t.last_name);

  const hasPhone = tPhone.length >= 10;
  const hasEmail = !!tEmail && tEmail.includes("@");
  if (!hasPhone && !hasEmail) return { severity: "none", matches: [], reason: null, rule: null };

  const candidates = pool.filter((l) => {
    if (l.id === target.id) return false;
    if (l.isTest) return false;
    if (!l.extracted) return false;
    return true;
  });

  const strongMatches = candidates.filter((l) => {
    const e = l.extracted!;
    const p = deps.normalizePhone(e.phone);
    const em = normalizeEmail(e.email);

    const phoneSame = hasPhone && p.length >= 10 && p === tPhone;
    const emailSame = hasEmail && !!em && em === tEmail;

    if (!phoneSame && !emailSame) return false;

    const bothHaveNames =
      (!!normalizeName(e.first_name) || !!normalizeName(e.last_name)) &&
      (!!tFirst || !!tLast);

    return bothHaveNames ? namesCompatible(e, tFirst, tLast) : true;
  });

  if (strongMatches.length > 0) {
    return {
      severity: "block",
      matches: strongMatches,
      reason: "Possible duplicate: same phone or email.",
      rule: "strong_key_match",
    };
  }

  const phoneOnlyWarn = candidates.filter((l) => {
    const e = l.extracted!;
    const p = deps.normalizePhone(e.phone);
    return hasPhone && p.length >= 10 && p === tPhone;
  });

  if (phoneOnlyWarn.length > 0) {
    return {
      severity: "warn",
      matches: phoneOnlyWarn,
      reason: "This phone number appears on another lead. Consider merging.",
      rule: "phone_only_warn",
    };
  }

  return { severity: "none", matches: [], reason: null, rule: null };
}